import React, { useState, useEffect } from 'react';
import { Typography, Grid, Theme } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FlipSliderTheme } from 'src/_common/quillElements/slider/FlipSlider';
import HideInMobile from 'src/_common/quillElements/fragment/HideinMobile';
import { detectAgent } from 'core/utils';
import { useDispatch, useSelector } from 'react-redux';
import { changeLengthGaugeAction } from 'src/summarizer/actions/controlActions';
import ShowInMobile from 'src/_common/quillElements/fragment/ShowInMobile';
import { RootState } from 'src/_common/models/redux/store';
import { openToastr } from 'src/_common/handlers/services';
import { useRouter } from 'next/router';
import { summarizeActionForSelectedText } from 'src/summarizer/actions/quillActions';

interface StyleProps {
  isCoWriter: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>((theme) =>
  createStyles({
    flipperTextContainer: {
      borderRadius: '8px',
      border: `1px ${theme.palette.divider} solid`,
      padding: '10px 7px',
      margin: 'auto',
      fontSize: '12px',
      maxWidth: '80%',
      '& strong': {
        display: 'block',
        fontWeight: 'bold',
        fontSize: '14px',
        width: '100%',
      },
      [theme.breakpoints.down(375)]: {
        fontSize: '8px',
        '& strong': {
          fontSize: '12px',
        },
      },
    },

    flipperContainer: {
      paddingLeft: '12px',
    },

    flipperInnerWrapper: {
      margin: '0 12px',
      minWidth: ({ isCoWriter }) => (isCoWriter ? 100 : 120),
    },

    summrizerSliderBox: {
      borderColor: theme.palette.divider,
      borderStyle: 'solid',
      borderRadius: '100px',
      flexWrap: 'nowrap',
      // marginTop: '-2px',
      height: '34px',
      padding: ({ isCoWriter }) => (isCoWriter ? '0 6px' : '0 12px'),
    },

    qbpSemibold: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '15px',
      display: 'block',
      textAlign: 'center',
      [theme.breakpoints.down(375)]: {
        fontSize: '12px',
      },
    },
    // tooltip: {
    //   backgroundColor: '#E5E5E5',
    //   color: 'black',
    //   width: '165px',
    //   padding: '6px 2px',
    //   top: 2,
    // },
    // tooltipArrow: {
    //   color: '#E5E5E5',
    // },
  })
);

interface IProps {
  currentValue: number;
  sliderWidth?: string;
  values: {
    min: number;
    max?: number;
    marks?: { value: number; label?: string }[];
  };
  disabled?: boolean;
  step?: number | null;
  selectedTextCowriter?: string;
}

function SummaryRatio(props: IProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const isCoWriter = router.pathname.includes('/co-writer');
  const classes = useStyles({ isCoWriter });
  const [isMobile, setIsMobile] = useState(false);
  const [oldSliderValue, setOldSliderValue] = useState(props.currentValue);
  const isQuilling = useSelector((state: RootState) => state.smzr.controls.isQuilling);
  const summarizerOriginalText = useSelector((state: RootState) => state.smzr.output.origText);
  // const selectedText = props.selectedTextCowriter;

  useEffect(() => setIsMobile(!!detectAgent().isMobile));

  /**
   * Debouncing changeLengthGaugeAction dispatch for async UI updates.
   */
  // useEffect(() => {
  //   if (!isQuilling && router.pathname === '/co-writer') {
  //     clearTimeout(timer);
  //     timer = setTimeout(() => {
  //       dispatch(changeLengthGaugeAction(oldSliderValue as number, summarizerOriginalText));
  //     }, 200);
  //   }
  // }, [oldSliderValue]);

  /* const getSliderTooltip = () => (
    <HideInMobile>
      <Grid container direction="row" justify="space-between">
        <Grid item style={{ textAlign: 'center', maxWidth: '60px' }}>
          <Typography variant="inherit" display="block">
            <b>Few</b> Sentences
          </Typography>
        </Grid>
        <Grid item style={{ textAlign: 'center', maxWidth: '60px' }}>
          <Typography variant="inherit" display="block">
            <b>More </b> Sentences
          </Typography>
        </Grid>
      </Grid>
    </HideInMobile>
  ); */
  return (
    <>
      <ShowInMobile style={{ width: '100%' }}>
        <Grid container className={classes.flipperTextContainer}>
          {/* <div style={{ maxWidth: isMobile ? 'auto' : '320px' }}>  </div> */}
          <Grid item xs={12} sm={12} md={12}>
            <Typography className={classes.qbpSemibold}>Summary Length</Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <ShowInMobile>
              <Grid container style={{ textAlign: 'center' }}>
                <Grid item xs={3} sm={3} md={3}>
                  <strong>Less </strong> Sentences
                </Grid>

                <Grid item xs={6} sm={6} md={6}>
                  {/* <Tooltip
                    classes={{
                      tooltip: classes.tooltip,
                      arrow: classes.tooltipArrow,
                    }}
                    arrow
                    placement="top"
                    title={getSliderTooltip()}> */}
                  <FlipSliderTheme
                    onChange={(_, newValue) => {
                      _.stopPropagation();
                      if (newValue !== oldSliderValue) {
                        if (isQuilling) {
                          openToastr('Summarizing underway...', 3000);
                          return;
                        }
                        setOldSliderValue(newValue as number);
                      }
                    }}
                    onChangeCommitted={(_, newValue) => {
                      dispatch(changeLengthGaugeAction(newValue as number));
                      if (!isQuilling && router.pathname.includes('/co-writer')) {
                        dispatch(summarizeActionForSelectedText(summarizerOriginalText));
                      }
                    }}
                    aria-label="summary length gauge"
                    value={oldSliderValue}
                    max={props.values.max}
                    min={props.values.min}
                    {...(props.step && { step: props.step })}
                    marks={props.values.marks ? props.values.marks : false}
                    disabled={props.disabled}
                  />
                  {/* </Tooltip> */}
                </Grid>

                <Grid item xs={3} sm={3} md={3}>
                  <strong>More </strong> Sentences
                </Grid>
              </Grid>
            </ShowInMobile>
          </Grid>
        </Grid>
      </ShowInMobile>
      <HideInMobile>
        <Grid
          container
          // columnSpacing={!isMobile ? 2 : 0}
          alignItems="center"
          direction={isMobile ? 'column' : 'row'}
          className={classes.summrizerSliderBox}
          style={{
            borderWidth: isMobile ? 'none' : '1px',
          }}
        >
          <Grid item>
            <Typography
              style={{ fontSize: '12px' }}
              className="qbp-semibold"
              variant="body2"
              color="textSecondary"
            >
              Summary Length
            </Typography>
          </Grid>
          <Grid item className={classes.flipperContainer}>
            <Grid container alignItems="center">
              <Grid item>
                <Typography
                  style={{ fontSize: '10px' }}
                  className="qbp-semibold"
                  variant="body2"
                  color="textSecondary"
                >
                  Short
                </Typography>
              </Grid>
              <Grid
                className={classes.flipperInnerWrapper}
                item
                style={{
                  width: props.sliderWidth,
                  // padding: '0 0px 23px',
                  position: 'relative',
                }}
              >
                {/* <Tooltip
    classes={{
      tooltip: classes.tooltip,
      arrow: classes.tooltipArrow,
    }}
    arrow
    placement="top"
    title={getSliderTooltip()}> */}
                <FlipSliderTheme
                  onChange={(_, newValue) => {
                    if (newValue !== oldSliderValue) {
                      _.stopPropagation();
                      if (isQuilling) {
                        openToastr('Summarizing underway... ', 3000);
                        return;
                      }
                      setOldSliderValue(newValue as number);
                    }
                  }}
                  onChangeCommitted={(_, newValue) => {
                    dispatch(changeLengthGaugeAction(newValue as number));
                    if (!isQuilling && router.pathname.includes('/co-writer')) {
                      dispatch(summarizeActionForSelectedText(summarizerOriginalText));
                    }
                  }}
                  aria-label="summary length gauge"
                  value={oldSliderValue}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    margin: 'auto',
                  }}
                  max={props.values.max}
                  min={props.values.min}
                  {...(props.step && { step: props.step })}
                  marks={props.values.marks ? props.values.marks : false}
                  disabled={props.disabled}
                />
                {/* </Tooltip> */}
              </Grid>
              <Grid item>
                <Typography
                  style={{ fontSize: '10px' }}
                  className="qbp-semibold"
                  variant="body2"
                  color="textSecondary"
                >
                  Long
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </HideInMobile>
    </>
  );
}
export default SummaryRatio;
